import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, File, Folder, FolderOpen } from 'lucide-react';

/**
 * Keep only the branches that match `query`, preserving ancestors so a deep
 * match stays reachable. A directory whose own name matches keeps all of its
 * children.
 */
function filterTree(items, needle) {
  if (!needle) return items;
  const result = [];
  for (const item of items) {
    const selfMatch = item.name.toLowerCase().includes(needle);
    if (item.type === 'directory') {
      const children = filterTree(item.children || [], needle);
      if (selfMatch) result.push(item);
      else if (children.length > 0) result.push({ ...item, children });
    } else if (selfMatch) {
      result.push(item);
    }
  }
  return result;
}

/** Depth-first flatten of everything currently visible — the keyboard order. */
function flatten(items, expanded, level = 0, out = []) {
  for (const item of items) {
    out.push({ item, level });
    if (item.type === 'directory' && expanded.has(item.path)) {
      flatten(item.children || [], expanded, level + 1, out);
    }
  }
  return out;
}

function collectDirPaths(items, out = []) {
  for (const item of items) {
    if (item.type === 'directory') {
      out.push(item.path);
      collectDirPaths(item.children || [], out);
    }
  }
  return out;
}

/**
 * The file navigation of the knowledge explorer (spec §10.5).
 *
 * A real ARIA tree: single tab stop, roving focus, arrow keys move and
 * expand/collapse, Home/End jump, Enter/Space activate. State rides on
 * `aria-expanded`/`aria-selected`/`aria-level`, never on class names.
 *
 * @param {object} props
 * @param {{name:string,path:string,type:'file'|'directory',children?:any[]}[]} props.tree
 * @param {string} [props.query] - live filter from the explorer search field.
 * @param {string|null} [props.selectedPath]
 * @param {(item: {name:string,path:string,type:string}) => void} props.onSelect
 * @param {string} props.label - accessible name for the tree.
 */
export function FileTree({ tree, query = '', selectedPath = null, onSelect, label }) {
  const { t } = useTranslation();
  const needle = query.trim().toLowerCase();
  const filtered = useMemo(() => filterTree(tree || [], needle), [tree, needle]);

  const [expanded, setExpanded] = useState(() => {
    const first = (tree || []).filter((item) => item.type === 'directory').map((item) => item.path);
    return new Set(first);
  });
  const [activePath, setActivePath] = useState(selectedPath);
  const containerRef = useRef(null);

  // While filtering, every surviving directory is open — otherwise the matches
  // the user is looking at would be hidden behind collapsed parents.
  const effectiveExpanded = useMemo(
    () => (needle ? new Set(collectDirPaths(filtered)) : expanded),
    [needle, filtered, expanded]
  );

  const rows = useMemo(() => flatten(filtered, effectiveExpanded), [filtered, effectiveExpanded]);

  useEffect(() => {
    if (selectedPath) setActivePath(selectedPath);
  }, [selectedPath]);

  // Keep the roving tab stop on a row that still exists after filtering.
  useEffect(() => {
    if (rows.length === 0) return;
    if (!rows.some((row) => row.item.path === activePath)) {
      setActivePath(rows[0].item.path);
    }
  }, [rows, activePath]);

  const toggle = (path) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const focusRow = (path) => {
    setActivePath(path);
    const node = containerRef.current?.querySelector(`[data-path="${CSS.escape(path)}"]`);
    node?.focus();
  };

  const handleKeyDown = (event, row) => {
    const index = rows.findIndex((candidate) => candidate.item.path === row.item.path);
    const isDir = row.item.type === 'directory';
    const isOpen = effectiveExpanded.has(row.item.path);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (index < rows.length - 1) focusRow(rows[index + 1].item.path);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (index > 0) focusRow(rows[index - 1].item.path);
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (isDir && !isOpen) toggle(row.item.path);
        else if (isDir && index < rows.length - 1) focusRow(rows[index + 1].item.path);
        break;
      case 'ArrowLeft': {
        event.preventDefault();
        if (isDir && isOpen) {
          toggle(row.item.path);
          break;
        }
        const parent = [...rows.slice(0, index)].reverse().find((candidate) => candidate.level < row.level);
        if (parent) focusRow(parent.item.path);
        break;
      }
      case 'Home':
        event.preventDefault();
        if (rows.length > 0) focusRow(rows[0].item.path);
        break;
      case 'End':
        event.preventDefault();
        if (rows.length > 0) focusRow(rows[rows.length - 1].item.path);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (isDir) toggle(row.item.path);
        onSelect?.(row.item);
        break;
      default:
        break;
    }
  };

  if (rows.length === 0) {
    return (
      <p className="etch-knowledge__rail-empty">{needle ? t('knowledge.noSearchResults') : t('ide.noFiles')}</p>
    );
  }

  return (
    <ul
      ref={containerRef}
      className="etch-knowledge__tree"
      role="tree"
      aria-label={label}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={-1}
    >
      {rows.map((row) => {
        const { item, level } = row;
        const isDir = item.type === 'directory';
        const isOpen = effectiveExpanded.has(item.path);
        const isSelected = selectedPath === item.path;
        const Icon = isDir ? (isOpen ? FolderOpen : Folder) : File;

        return (
          <li key={item.path} role="none">
            <div
              role="treeitem"
              data-path={item.path}
              className="etch-file-row"
              aria-level={level + 1}
              aria-selected={isSelected}
              aria-expanded={isDir ? isOpen : undefined}
              data-kind={isDir ? 'directory' : 'file'}
              tabIndex={activePath === item.path ? 0 : -1}
              style={{ '--etch-tree-indent': level }}
              onClick={() => {
                setActivePath(item.path);
                if (isDir) toggle(item.path);
                onSelect?.(item);
              }}
              onKeyDown={(event) => handleKeyDown(event, row)}
            >
              <span className="etch-file-row__twisty" aria-hidden="true">
                {isDir ? isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} /> : null}
              </span>
              <span className="etch-file-row__icon" aria-hidden="true">
                <Icon size={14} />
              </span>
              <span className="etch-file-row__name">{item.name}</span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default FileTree;
